import Block from "@sanity/block-content-to-react"
import { type PageProps, type HeadProps, graphql } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import Container from "@/components/Container"
import DiagonalFrosting from "@/components/DiagonalFrosting"
import Grid from "@/components/Grid"
import Layout from "@/components/Layout"
import LineRule from "@/components/LineRule"
import Logo from "@/components/Logo"
import MovingGradientLite from "@/components/MovingGradientLite"
import Rows from "@/components/Rows"
import Section from "@/components/Section"
import SEOMeta from "@/components/SEOMeta"
import SingleInputSignupForm from "@/components/SingleInputSignupForm"
import StoryBlockCard from "@/components/StoryBlockCard"
import * as styles from "@/styles/modules/pages/Home.module.scss"

type Blocks = [...Queries.HomePageQuery["blocks"]["nodes"]]
export type Block = Blocks[number]

export const Head = ({ location, data }: HeadProps<Queries.HomePageQuery>) => {
  const seo = data.homepage.nodes[0].homepageContent.seo
  return <SEOMeta data={seo} pathname={location.pathname} />
}

export default function HomePage({ data }: PageProps<Queries.HomePageQuery>) {
  const { headline, subhead, featuredImage } =
    data.homepage.nodes[0].homepageContent || {}

  const catchUpCategories = data.catchUpCategories.nodes.map(cat => ({
    slug: cat.slug.current,
    themeRgb: cat.theme.rgb,
  }))

  const latestDate = data.blocks.nodes.reduce<number>((latest, block) => {
    const blockDate = new Date(block.date).getTime()
    // Skip blocks with future dates
    if (blockDate > Date.now()) {
      return latest
    }

    if (blockDate > latest) {
      latest = blockDate
    }

    return latest
  }, 1)

  const latestBlocks = data.blocks.nodes.reduce<Blocks>((acc, block) => {
    if (new Date(block.date).getTime() === latestDate) {
      acc.push(block)
      return acc
    }
    return acc
  }, [])

  const discussionBlock = latestBlocks.find(
    block => block.category?.slug === "discussion",
  )

  const catchUpBlocks = latestBlocks.filter(block =>
    catchUpCategories.find(cat => cat.slug === block.category?.slug),
  )

  const featuredImg = getImage(featuredImage!.asset)

  return (
    <Layout forceHeaderBkg showSignupBanner={false}>
      <main className={styles.main}>
        <section className={styles.wrapper}>
          <Container>
            <div className={styles.content}>
              <Logo className={styles.logo} />
              <div className={styles.headingText}>
                <h1 className={`display ${styles.headline}`}>{headline}</h1>
                <Block className="block" blocks={subhead?._rawData} />
              </div>
              <SingleInputSignupForm
                wrapperClass={styles.signuUpFormWrapper}
                formName="Homepage Signup Form"
              />

              <p className={`caption ${styles.btnCaption}`}>
                Join 75,000+ readers. 100% free, unsubscribe anytime
              </p>
            </div>
          </Container>
          <div className={styles.mediaContainer}>
            <MovingGradientLite />
            <DiagonalFrosting className={styles.diagonalFrosting} />
            <div className={styles.imageWrapper}>
              <GatsbyImage image={featuredImg!} alt="" />
            </div>
          </div>
        </section>

        <Section shape="squiggles" first anchorId="latest">
          <Container>
            <h2 className={styles.storySectionHeadline}>
              👋 Welcome! The news awaits...
            </h2>
            <Rows gap={6}>
              {discussionBlock && (
                <>
                  <LineRule label="Today's Discussion" />
                  <StoryBlockCard isFeatured {...discussionBlock} />
                  <LineRule label="And other top stories" />
                </>
              )}
              <Grid
                mobile={1}
                mobileLg={2}
                desktop={3}
                rowGap={48}
                columnGap={48}
              >
                {catchUpBlocks.map(block => {
                  return <StoryBlockCard key={block._id} {...block} />
                })}
              </Grid>
            </Rows>
          </Container>
        </Section>
      </main>
    </Layout>
  )
}

export const query = graphql`
  query HomePage {
    homepage: allSanityHomepage {
      nodes {
        homepageContent {
          headline
          featuredImage {
            asset {
              gatsbyImageData
            }
          }
          subhead {
            _rawData
          }
          alreadySubscribedHeadline
          alreadySubscribedSubhead {
            _rawData
          }
          seo {
            ...SEOObjectData
          }
        }
      }
    }
    catchUpCategories: allSanityBlockCategories(
      filter: { group: { slug: { current: { eq: "catch-up" } } } }
    ) {
      nodes {
        slug {
          current
        }
        theme {
          rgb {
            r
            g
            b
          }
        }
      }
    }
    discussionCategory: allSanityBlockCategories(
      filter: { slug: { current: { eq: "discussion" } } }
    ) {
      nodes {
        slug {
          current
        }
        theme {
          _rawRgb
        }
      }
    }
    blocks: allStudioHubBlock(
      filter: {
        slug: { ne: null }
        category: {
          name: {
            in: [
              "Business & Markets"
              "U.S."
              "Media & Entertainment"
              "Science & Emerging Tech"
              "Politics"
              "World"
              "Sports"
              "Discussion"
              "DONUT Holes"
            ]
          }
        }
      }
      sort: { date: DESC }
      limit: 30
    ) {
      nodes {
        slug
        category {
          slug
          name
        }
        date
        elements
        tlDrExcerpt
        author
        _id
      }
    }
  }
`
